/**
 * Shared Action Center item composer (§7.14 badge composer — Track 4 Slice A;
 * SCOPING_ONBOARDING_FLOW.md §7.14, POLICY_ACTION_CENTER.md).
 *
 * ONE composition of "every action item in the user's court," consumed by:
 * - ActionCenterInbox (mode "inbox") — the full list, RACI via the
 *   per-project Gantt fan-out (useMyRaciTasks) on AC mount;
 * - the app-wide LeftNav badge (mode "badge") — same set, but RACI comes
 *   from the lightweight /api/action-items/raci-open ID-list (the 2026-07-08
 *   lazy-source audit: RACI is the sole AC-mount-only heavy source; a badge
 *   must not force the fan-out app-wide).
 *
 * The composition logic previously lived inline in ActionCenterInbox; it was
 * EXTRACTED here verbatim (not rewritten) so badge and list literally share
 * one source and can never diverge (§7.14 crux ruling, option a). The badge's
 * RACI ids are additionally intersected with the same engagement-derived slot
 * set the inbox uses, giving the acceptance guard: badge RACI ids ⊆ inbox
 * RACI items on AC mount.
 *
 * Obligations-only per §7.1 — the belonging-side "Life4Earth {rung}" identity
 * role contributes NO items here. Checklist rows and org-guidelines Notices
 * are composed INSIDE this one set (no separate badge terms; no double count).
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppTranslation } from "../i18n/useAppTranslation";
import i18n from "../i18n/config";
import { getAllWorkflows, subscribeWorkflows } from "../workflow";
import { useApiRoleRhythmInstances } from "../api/useApiRoleRhythmInstances";
import { useApiMyMiniProjectTasks } from "../api/useApiMyMiniProjectTasks";
import { useApiOnboarding } from "../api/useApiOnboarding";
import { useApiMyFollows } from "../api/useApiMyFollows";
import {
  useApiCommunityGuidelines,
  useApiCommunityGuidelinesMap,
} from "../api/useApiCommunityGuidelines";
import { computeActiveOrgGuids } from "../Data/Onboarding/orgDuties";
import { STANDARDS_CURRENT_VERSION } from "../content/legal/standardsConstants";
import { useOnboardingCopy, resolveCopyOverride } from "../api/onboardingCopy";
import { classifyInstanceLateness } from "../Data/DataReliability/perInstanceState";
import { excludeHse } from "../Data/missionSubkpiFilter";
import {
  getArchetypeConfig,
  listIncompleteMilestones,
} from "../Data/Onboarding/archetypeConfig";
import { evaluateMilestones } from "../Data/Onboarding/milestoneEvaluator";
import { useEngagementEntries } from "../Data/Engagement/useEngagementEntries";
import { useMyRaciTasks, type MyRaciTask, type RaciRoleRef } from "../api/useMyRaciTasks";
import { fetchOpenRaciItems, type OpenRaciItem } from "../api/actionItemsRaciOpen";
import {
  buildNameLookup,
  eventToActionItem,
  isWorkflowResolved,
  rhythmToActionItem,
  miniProjectTaskToActionItem,
  raciTaskToActionItem,
  dataRowToActionItems,
  milestoneToActionItem,
  gettingStartedToActionItems,
  promotionNoticeToActionItem,
  typeKeyFor,
  ROLE_BASE_KEY,
  type ActionItem,
  type DataSection,
} from "../Pages/ContentApprovals/actionItems";

const DEFAULT_GRACE_DAYS = 2;

/** Stable empty role set — badge mode never triggers the Gantt fan-out. */
const NO_RACI_ROLES: RaciRoleRef[] = [];

/** Badge-mode refresh cadence for the RACI ID-list (lightweight endpoint). */
const RACI_OPEN_POLL_MS = 5 * 60_000;

export type ActionItemComposerMode = "inbox" | "badge";

export interface ActionItemComposerInputs {
  mode: ActionItemComposerMode;
  currentUserGuid: string;
  users?: Record<string, any>[];
  projects?: Record<string, any>[];
  communities?: Record<string, any>[];
  relationships?: Record<string, any>[];
  data?: any[];
  communityMemberships?: Record<string, any>[];
  projectAcl?: Record<string, any>[];
  allProjectHeroLookup?: Map<string, string>;
  communityRoleAssignments?: Record<string, any>[];
}

/** Badge mode: synthesize the client MyRaciTask shape from an endpoint row so
 *  raciTaskToActionItem produces byte-identical ids/statuses to the inbox. */
function openRaciToMyRaciTask(r: OpenRaciItem): MyRaciTask {
  const today = new Date().toISOString().slice(0, 10);
  const daysToEnd = r.endDate
    ? Math.ceil(
        (new Date(r.endDate).getTime() - new Date(today).getTime()) / (1000 * 60 * 60 * 24),
      )
    : null;
  // computeUrgency (useMyRaciTasks), R-branch: endpoint rows are open
  // (percent < 100) by construction.
  const urgency =
    daysToEnd !== null && daysToEnd < 0
      ? ("overdue" as const)
      : daysToEnd !== null && daysToEnd <= 2
        ? ("imminent" as const)
        : ("upcoming" as const);
  return {
    taskGuid: r.taskGuid,
    projectGuid: r.projectGuid,
    roleSlotGuid: r.roleSlotGuid,
    taskName: r.taskName,
    wbsPath: "",
    startDate: r.startDate,
    endDate: r.endDate,
    percentComplete: r.percentComplete,
    raci: "R",
    urgency,
    done: false,
    acknowledged: false,
    inputProvided: false,
    consultationResponseText: null,
    assignedToName: null,
    daysSinceUpdate: null,
  };
}

export function useActionItemComposer(inputs: ActionItemComposerInputs) {
  const {
    mode,
    currentUserGuid,
    users,
    projects,
    communities,
    relationships,
    data,
    communityMemberships,
    projectAcl,
    allProjectHeroLookup,
    communityRoleAssignments,
  } = inputs;

  const { t } = useAppTranslation("pages");

  // Workflow-store mutation counter (module store → re-compose on change).
  const [version, setVersion] = useState(0);
  useEffect(() => subscribeWorkflows(() => setVersion((v) => v + 1)), []);
  const bumpVersion = useCallback(() => setVersion((v) => v + 1), []);

  const { instances, refetch: refetchRhythms } = useApiRoleRhythmInstances();
  const { tasks: mpTasks, refetch: refetchMpTasks } = useApiMyMiniProjectTasks();

  // SPEC_ROLE_ORGANIZED_INBOX §4a — the user's real roles (shared with the
  // Engagements list), which source the rail's role cards.
  const { entries: engagementEntries } = useEngagementEntries({
    currentUserGuid,
    projectData: data,
    allProjectHeroLookup,
    users,
    communities,
    projectAcl,
    communityRoleAssignments,
    communityRelationships: relationships,
  });

  // ── Gantt RACI ingestion (DEV-1368) ──────────────────────────────
  const fullProjectRoles = useMemo<RaciRoleRef[]>(() => {
    const out: RaciRoleRef[] = [];
    for (const e of engagementEntries) {
      if (e.typeTag !== "full-project" || e.source.type !== "role") continue;
      const d = e.source.data as any;
      const pg = d?.projectGuid;
      const slot = d?.roleSlotGuid;
      if (pg && slot) out.push({ projectGuid: String(pg), roleSlotGuid: String(slot) });
    }
    return out;
  }, [engagementEntries]);

  const raciSlotInfo = useMemo(() => {
    const m = new Map<
      string,
      { roleLabel: string; communityGuid: string | null; projectName: string }
    >();
    for (const e of engagementEntries) {
      if (e.typeTag !== "full-project" || e.source.type !== "role") continue;
      const slot = (e.source.data as any)?.roleSlotGuid;
      if (!slot) continue;
      m.set(String(slot).toUpperCase(), {
        roleLabel: e.roleLabel,
        communityGuid: e.groupCommunityGuid || e.owningCommunityGuid || null,
        projectName: e.entityName || "",
      });
    }
    return m;
  }, [engagementEntries]);

  // Inbox mode: the full per-project fan-out (AC mount only — the audit's
  // sole heavy source). Badge mode: no fan-out; the ID-list endpoint below.
  const { tasks: raciTasks } = useMyRaciTasks(
    mode === "inbox" ? fullProjectRoles : NO_RACI_ROLES,
    currentUserGuid,
  );

  // Badge mode: open-R ID-list (sibling of /api/action-items/mini-project-tasks).
  const [openRaci, setOpenRaci] = useState<OpenRaciItem[]>([]);
  useEffect(() => {
    if (mode !== "badge" || !currentUserGuid) {
      setOpenRaci([]);
      return;
    }
    let cancelled = false;
    const load = () =>
      fetchOpenRaciItems()
        .then((rows) => {
          if (!cancelled) setOpenRaci(rows || []);
        })
        .catch(() => {
          /* offline / endpoint not yet deployed — badge omits RACI until reachable */
        });
    load();
    const iv = window.setInterval(load, RACI_OPEN_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(iv);
    };
  }, [mode, currentUserGuid]);

  // Slice E (DEV-1368) — entity → the user's role label, for nesting authority/
  // notice events under the right role card.
  const eventRoleAttr = useMemo(() => {
    const proj = new Map<string, string>();
    const comm = new Map<string, string>();
    const commCount = new Map<string, number>();
    for (const e of engagementEntries) {
      const d = e.source.data as any;
      if (e.source.type === "role") {
        const pg = d?.projectGuid ? String(d.projectGuid).toUpperCase() : null;
        if (pg && !proj.has(pg)) proj.set(pg, e.roleLabel);
      } else if (e.source.type === "community") {
        const cgid = d?.entityGuid
          ? String(d.entityGuid).toUpperCase()
          : e.groupCommunityGuid
            ? e.groupCommunityGuid.toUpperCase()
            : null;
        if (cgid) {
          commCount.set(cgid, (commCount.get(cgid) || 0) + 1);
          comm.set(cgid, e.roleLabel);
        }
      }
    }
    for (const [k, n] of commCount) if (n > 1) comm.delete(k); // ambiguous → group level
    return { proj, comm };
  }, [engagementEntries]);

  // Onboarding (chunk A §7.7/§7.9) — checklist + promotion Notice inputs.
  const {
    archetype,
    achievedMilestones: onboardingFlags,
    milestoneDates: onboardingFlagDates,
    invitedCommunityGuid,
    recordMilestone: recordOnboardingMilestone,
    loading: onboardingLoading,
  } = useApiOnboarding(currentUserGuid);
  const isExplorer = archetype === "explorer";
  const gsActive = isExplorer || onboardingFlags.has("contributor_promoted");

  // §7.7 r1 item 3 — follow count computes LIVE (rung-basic pattern).
  const { followCount } = useApiMyFollows(gsActive);

  // §7.9 — org guidelines + the user's acknowledgment currency (invited org).
  const { guidelines, acknowledge: acknowledgeGuidelines } = useApiCommunityGuidelines(
    gsActive ? invitedCommunityGuid : null,
  );

  // Admin copy overrides (onboarding_copy, migration 265).
  const { copyMap } = useOnboardingCopy(gsActive);
  const tGettingStarted = useCallback(
    (k: string, vars?: Record<string, any>) =>
      resolveCopyOverride(copyMap, i18n.language || "en", `pages:${k}`, vars) ??
      t(k, vars as never),
    [copyMap, t],
  );

  // ── Resolvers ──
  const userMap = useMemo(
    () =>
      buildNameLookup(
        users,
        ["user_guid", "userGuid", "guid"],
        ["display_name", "displayName", "name", "email"],
      ),
    [users],
  );
  const projectMap = useMemo(
    () =>
      buildNameLookup(
        projects,
        ["project_GUID", "project_guid", "projectGuid", "guid"],
        ["project_name", "projectName", "name"],
      ),
    [projects],
  );
  const communityMap = useMemo(
    () => buildNameLookup(communities, ["entity_guid", "entityGuid", "guid"], ["name"]),
    [communities],
  );
  const communityTypeMap = useMemo(
    () =>
      buildNameLookup(
        communities,
        ["entity_guid", "entityGuid", "guid"],
        ["entity_type", "entityType"],
      ),
    [communities],
  );

  // Orgs the user belongs to, minus personal Workshops (shared helper —
  // orgDuties.ts — so every consumer computes the same set).
  const myOrgGuids = useMemo(
    () => computeActiveOrgGuids(currentUserGuid, communityMemberships, communities),
    [currentUserGuid, communityMemberships, communities],
  );
  const orgGuidelinesMap = useApiCommunityGuidelinesMap(myOrgGuids);

  // projectGuid → owning community GUID (data-entry attribution).
  const projectCommunityMap = useMemo(
    () =>
      buildNameLookup(
        projects,
        ["project_GUID", "project_guid", "projectGuid", "guid"],
        [
          "community_guid",
          "communityEntityGuid",
          "community_entity_guid",
          "ownerCommunityGuid",
          "owning_community_entity_guid",
          "owning_community_guid",
        ],
      ),
    [projects],
  );

  const resolveName = useCallback(
    (guid: string | null | undefined): string => {
      if (!guid) return "";
      const u = String(guid).toUpperCase();
      return userMap.get(u) || projectMap.get(u) || communityMap.get(u) || "";
    },
    [userMap, projectMap, communityMap],
  );

  const now = useMemo(() => {
    void version; // refresh "now" after a workflow-store mutation re-render
    return new Date();
  }, [version]);

  // ── Build active items (frontend join) ──
  const items = useMemo<ActionItem[]>(() => {
    void version; // getAllWorkflows() is read below; recompute on store mutation
    const me = String(currentUserGuid || "").toUpperCase();
    const out: ActionItem[] = [];
    if (!me) return out;
    const ctx = { currentUserGuid: me, resolveName, t: (k: string) => t(k), now };

    // Events (inbox: assigned to me, not yet resolved)
    for (const w of getAllWorkflows()) {
      const assigned = (w.assigned_to_guids || w.assigned_to_user_guids || []).map((g) =>
        String(g).toUpperCase(),
      );
      if (!assigned.includes(me)) continue;
      if (isWorkflowResolved(w)) continue;
      out.push(eventToActionItem(w, ctx));
    }

    // Rhythm task instances. Standing duties are excluded (continuous
    // responsibilities live on the per-role RhythmDisplayPanel).
    for (const inst of instances) {
      if (inst.taskKind === "standing_duty") continue;
      if (inst.status === "pending") continue;
      out.push(rhythmToActionItem(inst, { t: (k: string) => t(k), now }));
    }

    // DEV-1257. Mini-project tasks (server filters to open + mine).
    for (const row of mpTasks) {
      out.push(miniProjectTaskToActionItem(row, { t: (k: string) => t(k), now }));
    }

    // Data entry (per project + section, Q6)
    const asOf = now.toISOString().slice(0, 10);
    for (const row of data || []) {
      if (!row?.action_center_can_edit_kpi || !row?._isActiveProject) continue;
      const expected: any[] = row?.data_reliability_expected_instances || [];
      const classification: Record<string, string> = {};
      for (const inst of expected) {
        classification[String(inst?.instance_id || "").toUpperCase()] = classifyInstanceLateness({
          instance: inst,
          asOfDate: asOf,
          defaultGraceDays: DEFAULT_GRACE_DAYS,
        });
      }
      const countFor = (arr: any[], keyFn: (x: any) => string) => {
        let due = 0;
        let overdue = 0;
        for (const x of arr) {
          const s = classification[keyFn(x).toUpperCase()];
          if (s === "due") due += 1;
          else if (s === "overdue") overdue += 1;
        }
        return { due, overdue };
      };
      const counts: Record<DataSection, { due: number; overdue: number }> = {
        schedule: countFor(row?.action_center_schedule_instances || [], (x) =>
          String(x?.instance_guid || ""),
        ),
        finance: countFor(row?.action_center_finance_instances || [], (x) =>
          String(x?.instance_guid || ""),
        ),
        mission: countFor(excludeHse(row?.mission_subkpis || []), (x) =>
          String(x?.outcome_id || x?.subKpiKey || x?.instance_guid || x?.label || ""),
        ),
      };
      out.push(...dataRowToActionItems(row, counts, { t: (k: string) => t(k) }));
    }

    // System tasks (onboarding)
    if (me) {
      const currentUser = (users || []).find(
        (u: any) => String(u?.user_guid || "").toUpperCase() === me,
      );
      try {
        const achieved = evaluateMilestones({
          currentUserGuid,
          currentUser,
          communityMemberships,
          projectAcl,
          projects,
        } as any);
        if (isExplorer) {
          // §7.7 r1 — the Getting-started checklist IS the acclimation tour.
          const orgGuidUpper = invitedCommunityGuid
            ? String(invitedCommunityGuid).toUpperCase()
            : null;
          out.push(
            ...gettingStartedToActionItems(
              {
                profileComplete: achieved.has("complete_profile"),
                followCount,
                achievedFlags: onboardingFlags as ReadonlySet<string>,
                orgCommunityGuid: invitedCommunityGuid || null,
                orgCommunityName: orgGuidUpper
                  ? communityMap.get(orgGuidUpper) || null
                  : null,
                guidelines: guidelines
                  ? {
                      hasContent: !!guidelines.content,
                      acknowledged: guidelines.acknowledged,
                    }
                  : null,
                signupAt: currentUser?.created_at ?? null,
                standardsCurrent:
                  !!currentUser?.community_standards_accepted_at &&
                  (currentUser?.community_standards_version_accepted ?? 0) >=
                    STANDARDS_CURRENT_VERSION,
              },
              { t: tGettingStarted },
            ),
          );
        } else {
          // Legacy generic milestone stream.
          const archetypeConfig = getArchetypeConfig("explorer");
          const incomplete = listIncompleteMilestones(archetypeConfig, achieved) as any[];
          for (const m of incomplete) {
            if (m?.severity === "mandatory" || m?.severity === "recommended") {
              out.push(milestoneToActionItem(m, { t: (k: string) => t(k) }));
            }
          }
        }
        // §7.7 r2 — one-time Contributor celebration Notice.
        if (
          onboardingFlags.has("contributor_promoted") &&
          !onboardingFlags.has("contributor_promotion_ack")
        ) {
          out.push(
            promotionNoticeToActionItem({
              t: (k: string) => t(k),
              promotedAt: onboardingFlagDates["contributor_promoted"] ?? null,
            }),
          );
        }
      } catch {
        /* milestone evaluator inputs may be empty on first render; skip */
      }
    }

    // ── General org-guidelines Notices (Skip 2026-07-08) ──
    for (const g of myOrgGuids) {
      if (
        isExplorer &&
        invitedCommunityGuid &&
        g === String(invitedCommunityGuid).toUpperCase()
      ) {
        continue;
      }
      const gl = orgGuidelinesMap.get(g);
      if (!gl || !gl.content || gl.acknowledged) continue;
      const orgName =
        communityMap.get(g) ||
        tGettingStarted("actionCenter.inbox.gettingStarted.orgFallback");
      const publishedAt = gl.updatedAt ? new Date(gl.updatedAt) : null;
      out.push({
        id: `orggl:${g}`,
        kind: "notice",
        typeKey: typeKeyFor("notice"),
        source: orgName,
        roleGroupKey: `community:${g}`,
        roleGroupLabel: orgName,
        pillar: "enabler",
        communityGuid: g,
        subject: tGettingStarted(
          "actionCenter.inbox.gettingStarted.readGuidelines.label",
          { org: orgName },
        ),
        dateLabel: "—",
        dateSort: Number.MAX_SAFE_INTEGER,
        addedLabel:
          publishedAt && !Number.isNaN(publishedAt.getTime())
            ? publishedAt.toLocaleDateString(undefined, { month: "short", day: "numeric" })
            : "—",
        addedSort:
          publishedAt && !Number.isNaN(publishedAt.getTime())
            ? publishedAt.getTime()
            : undefined,
        status: "needs_response",
        overdue: false,
        base: 2, // TYPE_BASE.notice — lowest event priority
        overdueDays: 0,
        dueInDays: null,
        ageDays: 0,
        checklistKey: "org_guidelines_member",
        milestone: {
          checklistKey: "org_guidelines_member",
          nudgeText: tGettingStarted(
            "actionCenter.inbox.gettingStarted.readGuidelines.nudge",
          ),
          communityGuid: g,
        },
      });
    }

    // Gantt RACI tasks (DEV-1368) — R (Responsible) only: the DO-list rule.
    // Inbox mode: client fan-out tasks. Badge mode: the open-R ID-list,
    // intersected with the SAME engagement-derived slot set (⊆ guard).
    const raciSource: MyRaciTask[] =
      mode === "inbox"
        ? raciTasks
        : openRaci
            .filter((r) => raciSlotInfo.has(r.roleSlotGuid.toUpperCase()))
            .map(openRaciToMyRaciTask);
    for (const rt of raciSource) {
      if (rt.raci !== "R") continue;
      const item = raciTaskToActionItem(rt, { t: (k: string) => t(k) });
      const info = raciSlotInfo.get(rt.roleSlotGuid.toUpperCase());
      if (info) {
        item.role = info.roleLabel;
        if (info.projectName) item.source = info.projectName;
        if (info.communityGuid) {
          item.roleGroupKey = `community:${info.communityGuid.toUpperCase()}`;
          item.roleGroupLabel =
            communityMap.get(info.communityGuid.toUpperCase()) || item.roleGroupLabel;
        }
      }
      out.push(item);
    }

    // Attribute data-entry items to their project's owning community.
    for (const it of out) {
      if (it.kind === "data" && it.data) {
        const og = projectCommunityMap.get(String(it.data.projectGuid).toUpperCase());
        if (og) {
          it.roleGroupKey = `community:${og.toUpperCase()}`;
          it.roleGroupLabel = communityMap.get(og.toUpperCase()) || it.roleGroupLabel;
        }
      }
    }

    // Slice E (DEV-1368) — event attribution.
    for (const it of out) {
      const w = it.workflow;
      if (!w) continue;
      if (it.kind !== "approval" && it.kind !== "governance" && it.kind !== "notice") continue;
      let cg: string | null = null;
      if (w.target_type === "community") {
        cg = String(w.target_guid || "").toUpperCase() || null;
      } else if (w.target_type === "project") {
        const og = projectCommunityMap.get(String(w.target_guid || "").toUpperCase());
        cg = og ? og.toUpperCase() : null;
      }
      if (cg) {
        it.roleGroupKey = `community:${cg}`;
        it.roleGroupLabel = communityMap.get(cg) || it.roleGroupLabel;
        it.communityGuid = cg;
        if (w.target_type === "project") {
          const rl = eventRoleAttr.proj.get(String(w.target_guid || "").toUpperCase());
          if (rl) it.role = rl;
        } else if (w.target_type === "community") {
          const rl = eventRoleAttr.comm.get(cg);
          if (rl) it.role = rl;
        }
      } else if (it.kind === "notice") {
        it.roleGroupKey = ROLE_BASE_KEY;
      }
      // else (approval / governance, unresolved) → undefined → "Other".
    }
    return out;
  }, [
    version,
    mode,
    instances,
    mpTasks,
    data,
    users,
    projects,
    communityMemberships,
    projectAcl,
    currentUserGuid,
    resolveName,
    now,
    t,
    projectCommunityMap,
    communityMap,
    raciTasks,
    openRaci,
    raciSlotInfo,
    eventRoleAttr,
    // Async-hook data feeding the Getting-started checklist + promotion
    // Notice (CLAUDE.md hook-deps rule).
    isExplorer,
    invitedCommunityGuid,
    followCount,
    onboardingFlags,
    onboardingFlagDates,
    guidelines,
    tGettingStarted,
    myOrgGuids,
    orgGuidelinesMap,
  ]);

  return {
    items,
    // Workflow-store version (handlers bump it after local mutations).
    version,
    bumpVersion,
    now,
    // Sources + refetchers the inbox's handlers/tray still need.
    instances,
    refetchRhythms,
    mpTasks,
    refetchMpTasks,
    engagementEntries,
    fullProjectRoles,
    raciSlotInfo,
    raciTasks,
    // Onboarding surface state.
    archetype,
    isExplorer,
    gsActive,
    onboardingFlags,
    onboardingFlagDates,
    invitedCommunityGuid,
    recordOnboardingMilestone,
    onboardingLoading,
    followCount,
    guidelines,
    acknowledgeGuidelines,
    tGettingStarted,
    // Resolvers / maps.
    userMap,
    projectMap,
    communityMap,
    communityTypeMap,
    myOrgGuids,
    orgGuidelinesMap,
    projectCommunityMap,
    resolveName,
  };
}
